// src/pages/ProjectDashboard.jsx

import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  doc,
  onSnapshot,
  collection,
  getDocs,
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from '../firebase/firebase';
import userPreferencesService from '../services/userPreferencesService';
import collaborationService from '../services/collaborationService';
import MapboxMap, { Marker, Source, Layer } from "react-map-gl/mapbox";
import "mapbox-gl/dist/mapbox-gl.css";
import circle from "@turf/circle";
import '../components/ProjectDashboardNotif.css';
import WhitePanel from '../components/WhitePanel';
import TeamSetupPanel from '../components/TeamSetupPanel';
import TeamSetupModal from '../components/TeamSetupModal';
import CompanyProjectAccessBanner from '../components/CompanyProjectAccessBanner';
import { useBranding } from '../contexts/BrandingContext';
import { MAP_STYLES, applyMapboxStyleExtras } from '../utils/mapboxStyle';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

const ROUTE_MAP = {
  activity:   'activity',
  attendance: 'attendance',
  gantt:      'gantt',
  design:     'design',
  documents:  'documents',
  snag:       'snag',
  emergency:  'emergency'
};

export default function ProjectDashboard() {
  const { projectId } = useParams();
  const navigate      = useNavigate();
  const { tokens } = useBranding();

  // get current user from Firebase Auth
  const auth = getAuth();
  const user = auth.currentUser;

  // Smart map caching to reduce API calls while preserving UX
  const [mapCache, setMapCache] = useState(new Map());
  const [mapKey, setMapKey] = useState(0);

  const [project, setProject]                 = useState(null);
  const [teams, setTeams]                     = useState([]);
  const [isTeamModalOpen, setIsTeamModalOpen] = useState(false);
  const [dashboardPanels, setDashboardPanels] = useState([]);
  const [permissions, setPermissions]         = useState({ canEdit: false, isOwner: false, isCollaborator: false, isCompanyOwner: false });
  const [mapType, setMapType]                 = useState('satellite');

  // Project doc (real-time) — keeps dashboard map preview in sync after e.g. Edit project details
  useEffect(() => {
    if (!projectId) return;
    const unsub = onSnapshot(
      doc(db, 'projects', projectId),
      (snap) => {
        if (!snap.exists()) {
          setProject(null);
          return;
        }
        const projectData = { id: snap.id, ...snap.data() };
        if (!projectData.status) {
          projectData.status = 'active';
        }
        setProject(projectData);
        const newMapKey = getCachedMapKey(
          projectId,
          projectData.latitude,
          projectData.longitude,
          projectData.gpsToleranceMeters
        );
        setMapKey(newMapKey);
        console.log('🗺️ Project map data updated:', {
          projectId,
          latitude: projectData.latitude,
          longitude: projectData.longitude,
          gpsTolerance: projectData.gpsToleranceMeters,
        });
      },
      (err) => console.error('Project snapshot error:', err)
    );
    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Check user permissions for this project
  useEffect(() => {
    if (!projectId || !user) return;
    
    const checkPermissions = async () => {
      try {
        const perms = await collaborationService.checkProjectPermissions(projectId, user.uid);
        setPermissions(perms);
        console.log('🔐 Project permissions:', perms);
      } catch (error) {
        console.error('Error checking project permissions:', error);
        setPermissions({ canEdit: false, isOwner: false, isCollaborator: false, isCompanyOwner: false });
      }
    };

    checkPermissions();
  }, [projectId, user]);

  // Cleanup map when component unmounts or project changes
  useEffect(() => {
    return () => {
      // Force cleanup of any lingering map instances
      setMapKey(0);
    };
  }, [projectId]);

  // fetch teams
  useEffect(() => {
    if (!projectId) return;
    getDocs(collection(db, `projects/${projectId}/teams`))
      .then(teamSnap => {
        setTeams(teamSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      })
      .catch(console.error);
  }, [projectId, isTeamModalOpen]);



  // Listen for dashboard layout changes from user preferences
  useEffect(() => {
    if (!user) return;

    // Add listener for dashboard layout updates
    const unsubscribe = userPreferencesService.addListener((panels) => {
      setDashboardPanels(panels);
      console.log('📱 Dashboard layout updated in real-time:', panels.length, 'panels');
    });

    // Load map type preference
    const loadMapType = async () => {
      try {
        const type = await userPreferencesService.loadMapType();
        setMapType(type);
        console.log('🗺️ Map type preference loaded:', type);
      } catch (error) {
        console.error('Error loading map type preference:', error);
      }
    };

    loadMapType();

    // Cleanup
    return () => {
      unsubscribe();
    };
  }, [user]);

  const handleTeamsUpdated = updated => setTeams(updated);

  // Helper function to safely convert Firestore timestamps
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Unknown date';
    if (timestamp.toDate) {
      return timestamp.toDate().toLocaleDateString('en-GB');
    }
    if (timestamp instanceof Date) {
      return timestamp.toLocaleDateString('en-GB');
    }
    return 'Unknown date';
  };


  // Calculate appropriate zoom level based on GPS tolerance radius
  // Custom zoom levels with specified thresholds
  const calculateZoomFromRadius = useCallback((radiusMeters) => {
    if (!radiusMeters) {
      return 18; // Default zoom when no radius specified
    }
    
    // Start at zoom 20 for small sites
    if (radiusMeters < 50) {
      return 20;
    }
    // 50-100m: zoom 18
    if (radiusMeters <= 100) {
      return 18;
    }
    // 100-200m: zoom 16 (zoomed out to show full circle, e.g., 150m radius)
    if (radiusMeters <= 200) {
      return 16;
    }
    // 200-500m: zoom 16
    if (radiusMeters <= 500) {
      return 14;
    }
    // 500-1000m: zoom 15
    if (radiusMeters <= 1000) {
      return 14;
    }
    // 1000-1500m: zoom 14
    if (radiusMeters <= 1500) {
      return 13;
    }
    // 1500-2000m: zoom 13 (stepping down from 14)
    if (radiusMeters <= 2000) {
      return 13;
    }
    // 2000-3000m: zoom 13
    if (radiusMeters <= 3000) {
      return 12;
    }
    // 3000m+: zoom 12 (capped for extremely large sites)
    return 12;
  }, []);

  // Smart map caching - preserve map instances when coordinates haven't changed
  const getCachedMapKey = useCallback((projectId, latitude, longitude, gpsTolerance) => {
    const cacheKey = `${projectId}-${latitude}-${longitude}-${gpsTolerance}`;
    
    // Check if we have a cached map with same coordinates
    if (mapCache.has(cacheKey)) {
      console.log('🗺️ Using cached map for project:', projectId);
      return cacheKey; // Return existing key, no re-render needed
    }
    
    // New coordinates or project - create fresh map
    const newKey = `${cacheKey}-${Date.now()}`;
    setMapCache(prev => new Map(prev).set(cacheKey, { key: newKey, timestamp: Date.now() }));
    console.log('🗺️ Creating new map for project:', projectId);
    
    return newKey; // Force re-render with new key
  }, [mapCache]);

  // Panel click handler. Always navigates through - each landing page already shows its
  // own read-only banner and enforces canEdit itself, so gating navigation here too just
  // means a view-only collaborator gets blocked from looking at a page they're allowed to see.
  const handlePanelClick = key => {
    if (key === 'documents') {
      navigate(`/project/${projectId}/documents`);
      return;
    }

    const sub = ROUTE_MAP[key];
    const url = sub
      ? `/project/${projectId}/${sub}`
      : `/project/${projectId}`;
    navigate(url);
  };

  if (!project) {
    return <p style={{ padding: 40 }}>Loading project...</p>;
  }

  return (
    <>
      <CompanyProjectAccessBanner
        projectId={projectId}
        canEdit={permissions.canEdit}
        isCompanyOwner={permissions.isCompanyOwner}
      />
      <div style={{ padding: 40, backgroundColor: 'var(--branding-app-surface)', minHeight: '100vh' }}>
      {/* Top-Row: Project Info, Map & Teams */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 24
      }}>
        <div style={{
          maxWidth: 'clamp(300px, 40%, 500px)',
          flexShrink: 1,
          minWidth: 0 // Allows text to shrink properly
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
            <h1 style={{
              fontSize: 'clamp(20px, 2.2vw, 22px)',
              fontWeight: 600,
              color: 'var(--cc-text-primary)',
              margin: 0,
              lineHeight: 1.2
            }}>
              {project.name}
            </h1>
            {project.status === 'closed' && (
              <span style={{
                background: '#6b7280',
                color: '#fff',
                fontSize: 11,
                padding: '3px 10px',
                borderRadius: 10,
                fontWeight: 500
              }}>
                CLOSED
              </span>
            )}
            {!permissions.canEdit && project.status !== 'closed' && (
              <span style={{
                background: '#f59e0b',
                color: '#fff',
                fontSize: 11,
                padding: '3px 10px',
                borderRadius: 10,
                fontWeight: 500
              }}>
                VIEW ONLY
              </span>
            )}
            {!permissions.isOwner && permissions.isCollaborator && (
              <span style={{
                background: 'var(--branding-app-primary)',
                color: 'var(--branding-app-primary-fg)',
                fontSize: 11,
                padding: '3px 10px',
                borderRadius: 10,
                fontWeight: 500
              }}>
                Collaborator
              </span>
            )}

          </div>
          {project.projectCode && (
            <p style={{ fontSize: 'clamp(12px, 1.3vw, 14px)', color: 'var(--cc-text-secondary)', marginBottom: 8, lineHeight: 1.4 }}>
              <strong>Project Code:</strong> {project.projectCode}
            </p>
          )}
          <p style={{ fontSize: 'clamp(12px, 1.3vw, 14px)', color: 'var(--cc-text-secondary)', marginBottom: 8, lineHeight: 1.4 }}>
            <strong>Address:</strong> {project.address}
          </p>
          <p style={{ fontSize: 'clamp(12px, 1.3vw, 14px)', color: 'var(--cc-text-secondary)', marginBottom: 16, lineHeight: 1.4 }}>
            <strong>Postcode:</strong> {project.postcode}
          </p>
          {project.status === 'closed' && project.closedByName && (
            <p style={{ fontSize: 12, color: 'var(--cc-text-muted)', marginBottom: 16 }}>
              <strong>Closed by:</strong> {project.closedByName} on {formatTimestamp(project.closedAt)}
            </p>
          )}
        </div>

        <div
          onClick={() => navigate(`/project/${projectId}/map`)}
          style={{
            background: 'var(--branding-app-surface-panel)',
            borderRadius: 16,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
            padding: 16,
            minWidth: 'clamp(280px, 24vw, 400px)',
            width: 'clamp(280px, 24vw, 400px)',
            maxWidth: 'clamp(280px, 24vw, 400px)',
            flexShrink: 0, // Prevent map from shrinking
            aspectRatio: '16/9',
            cursor: 'pointer',
            position: 'relative',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            overflow: 'hidden'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-4px)';
            e.currentTarget.style.boxShadow = '0 8px 30px rgba(0, 0, 0, 0.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.05)';
          }}
        >
          {/* Mini map preview */}
          {project.latitude && project.longitude && MAPBOX_TOKEN && (
            <div
              key={`map-container-${projectId}-${mapKey}`}
              style={{
                width: '100%',
                height: '100%',
                borderRadius: '12px',
                overflow: 'hidden',
                position: 'relative'
              }}
            >
              <div style={{
                width: '100%',
                height: '100%',
                overflow: 'hidden'
              }}>
                <MapboxMap
                  key={`${projectId}-${project.latitude}-${project.longitude}`}
                  mapboxAccessToken={MAPBOX_TOKEN}
                  initialViewState={{
                    latitude: project.latitude,
                    longitude: project.longitude,
                    zoom: calculateZoomFromRadius(project.gpsToleranceMeters)
                  }}
                  mapStyle={MAP_STYLES[mapType] || MAP_STYLES.satellite}
                  style={{ width: '100%', height: '100%' }}
                  interactive={false}
                  attributionControl={{ compact: true }}
                  onLoad={(e) => {
                    const map = e.target;
                    applyMapboxStyleExtras(map);
                    map.on('style.load', () => applyMapboxStyleExtras(map));
                  }}
                >
                  <Marker
                    key={`${projectId}-marker-${project.latitude}-${project.longitude}`}
                    longitude={project.longitude}
                    latitude={project.latitude}
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="#dc2626" />
                    </svg>
                  </Marker>

                  {/* Mini GPS tolerance circle */}
                  {project.gpsToleranceMeters && (() => {
                    const miniCircle = circle(
                      [project.longitude, project.latitude],
                      Number(project.gpsToleranceMeters) / 1000,
                      { steps: 64, units: 'kilometers' }
                    );
                    return (
                      <Source key={`${projectId}-circle-${project.gpsToleranceMeters}`} type="geojson" data={miniCircle}>
                        <Layer type="fill" paint={{ 'fill-color': '#60a5fa', 'fill-opacity': 0.2 }} />
                        <Layer type="line" paint={{ 'line-color': tokens.appPrimary, 'line-width': 1 }} />
                      </Source>
                    );
                  })()}
                </MapboxMap>
              </div>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
          <div
            onClick={() => navigate(`/project/${projectId}/teams`)}
            style={{
              background: 'var(--branding-app-surface-panel)',
              borderRadius: 12,
              boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
              padding: '20px 24px',
              minWidth: 'clamp(220px, 18vw, 320px)',
              maxHeight: 'clamp(300px, 25vh, 400px)',
              overflowY: 'auto',
              cursor: 'pointer',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 30px rgba(0,0,0,0.12)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 24px rgba(0,0,0,0.08)';
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
              <div
                style={{
                  fontWeight: 600,
                  color: 'var(--branding-app-surface-panel-fg)',
                  fontSize: 16
                }}
              >
                Teams
              </div>
              {permissions.canEdit && (
                <button
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent navigation when clicking + button
                    setIsTeamModalOpen(true);
                  }}
                  style={{
                    marginLeft: 'auto',
                    background: 'var(--branding-app-primary)',
                    color: 'var(--branding-app-primary-fg)',
                    border: 'none',
                    borderRadius: 8,
                    padding: '8px 16px',
                    fontWeight: 500,
                    cursor: 'pointer',
                    fontSize: 14
                  }}
                  title="Add Team"
                  aria-label="Add Team"
                >
                  +
                </button>
              )}
            </div>
            {teams.length === 0 ? (
              <div style={{ color: 'var(--branding-app-surface-panel-muted)', fontSize: 14 }}>
                No teams yet.
              </div>
            ) : (
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {teams.map(team => (
                  <li key={team.id} style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: 10
                  }}>
                    <span style={{
                      display: 'inline-block',
                      width: 14,
                      height: 14,
                      borderRadius: '50%',
                      background: team.color,
                      marginRight: 10,
                      border: '1px solid var(--branding-app-surface-panel-border)'
                    }}/>
                    <span style={{
                      fontWeight: 500,
                      color: 'var(--branding-app-surface-panel-fg)',
                      fontSize: 15
                    }}>
                      {team.name}
                    </span>
                    <span style={{
                      color: 'var(--branding-app-surface-panel-muted)',
                      marginLeft: 'auto',
                      fontSize: 13
                    }}>
                      {team.role}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* Dashboard Panels - Now using user preferences */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        gridTemplateRows: 'repeat(2, minmax(0, auto))',
        gap: 24,
        marginTop: 40,
        gridAutoFlow: 'row'
      }}>
        {dashboardPanels.map(panel => {
          return (
            <div
              key={panel.key}
              onClick={() => handlePanelClick(panel.key)}
              style={{
                cursor: 'pointer',
                width: '100%',
                minWidth: '0',
                maxWidth: '100%',
                overflow: 'hidden'
              }}
            >
              <WhitePanel
                title={panel.key === 'snag' ? 'Snags & variations' : panel.title}
                description={panel.description}
                projectId={projectId}
                panelType={panel.key}
              />
            </div>
          );
        })}
      </div>

      {/* Read-only notice for closed projects */}
      {project.status === 'closed' && (
        <div style={{
          background: '#fef3c7',
          border: '1px solid #f59e0b',
          borderRadius: 8,
          padding: 16,
          marginTop: 24,
          textAlign: 'center'
        }}>
          <p style={{
            margin: 0,
            color: '#92400e',
            fontSize: 14,
            fontWeight: 500
          }}>
            This project is closed and read-only. You can navigate to all sections and view all project data, but cannot make changes.
          </p>
        </div>
      )}

      {/* Permission notice for non-editors (company owners get CompanyProjectAccessBanner
          at the top of the page instead - this stays for regular pm_viewer collaborators) */}
      {!permissions.canEdit && !permissions.isCompanyOwner && project.status !== 'closed' && (
        <div style={{
          background: 'var(--cc-badge-bg)',
          border: '1px solid var(--branding-app-primary)',
          borderRadius: 8,
          padding: 16,
          marginTop: 24,
          textAlign: 'center'
        }}>
          <p style={{
            margin: 0,
            color: 'var(--cc-text-primary)',
            fontSize: 14,
            fontWeight: 500
          }}>
            You have view-only access to this project. Only the project owner and invited PM collaborators can make changes.
          </p>
        </div>
      )}

      {/* Team Setup Modal */}
      <TeamSetupModal
        open={isTeamModalOpen}
        onClose={() => setIsTeamModalOpen(false)}
      >
        <TeamSetupPanel
          onTeamsUpdated={handleTeamsUpdated}
          onDone={() => setIsTeamModalOpen(false)}
        />
      </TeamSetupModal>


      {/* CSS animations are now in ProjectDashboardNotif.css */}
      </div>
    </>
  );
}
